import React, { useEffect, useState } from 'react';

// Monotonic id so two diagrams rendering in the same millisecond never collide
// on mermaid's internal element id (Date.now() alone was racy).
let mermaidSeq = 0;

// Cache content → SVG so re-mounts and scroll-triggered re-renders never
// flash back to the "Rendering diagram…" placeholder. The cache is intentionally
// unbounded — diagram count per session is small and content strings are short.
const svgCache = new Map();

// Diagram artifacts are sometimes saved with the model's surrounding Markdown
// code fence still attached (```mermaid …\n```). Inline chat diagrams come
// through ReactMarkdown, which strips the fence for us — but the Artifacts panel
// passes the raw stored content straight here, so an un-stripped fence made
// mermaid.render fail to parse and the diagram silently fell back to raw text.
// Strip a single wrapping fence (with or without a language tag) defensively so
// both render paths behave the same.
export function stripCodeFence(raw) {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  const match = trimmed.match(/^```[^\n]*\n([\s\S]*?)\n?```$/);
  return match ? match[1].trim() : trimmed;
}

// Mermaid keeps a single global config + parser state, so it must only be
// imported and initialized once for the whole app — re-initializing on every
// diagram's render is wasteful and can race when several diagrams mount at the
// same time. We memoize the dynamic import and the initialize call here.
let mermaidPromise = null;
async function getMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then(({ default: mermaid }) => {
      // suppressErrorRendering stops mermaid from injecting its built-in
      // "bomb" error SVG into the DOM (orphaned at document.body) when a
      // diagram fails to parse — we render our own <pre> fallback instead.
      //
      // Theme: the app is a single light ("cream + teal") theme, so the old
      // `theme: 'dark'` rendered dark diagrams on a light page. We use the
      // customizable `base` theme wired to the site's palette (see index.css
      // CSS variables) so diagrams read as part of the product — soft teal
      // nodes, warm accents, readable dark text — and give the diagram kinds
      // we use most (pie, mindmap, journey, gitGraph, class/state colour
      // groups) a coherent multi-colour scale instead of a flat monochrome.
      mermaid.initialize({
        startOnLoad: false,
        suppressErrorRendering: true,
        theme: 'base',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        themeVariables: {
          // Primary node fill / border / text (flowchart default nodes).
          primaryColor: '#e4f0ed',
          primaryBorderColor: '#127c6d',
          primaryTextColor: '#26271f',
          // Secondary + tertiary fills (used for alternating groups, clusters).
          secondaryColor: '#fce8d4',
          secondaryBorderColor: '#c97d24',
          secondaryTextColor: '#26271f',
          tertiaryColor: '#eceae3',
          tertiaryBorderColor: '#d6d4ca',
          tertiaryTextColor: '#26271f',
          // Edges + labels.
          lineColor: '#6b6d64',
          textColor: '#26271f',
          titleColor: '#26271f',
          edgeLabelBackground: '#f4f3ee',
          mainBkg: '#e4f0ed',
          // Subgraph / cluster boxes.
          clusterBkg: '#f0efe8',
          clusterBorder: '#d6d4ca',
          // Notes (sequence/class/state diagrams).
          noteBkgColor: '#fff3cf',
          noteTextColor: '#26271f',
          noteBorderColor: '#e0b94d',
          // Sequence-diagram actors + signals.
          actorBkg: '#e4f0ed',
          actorBorder: '#127c6d',
          actorTextColor: '#26271f',
          signalColor: '#6f6e66',
          signalTextColor: '#26271f',
          labelBoxBkgColor: '#e4f0ed',
          labelBoxBorderColor: '#127c6d',
          activationBkgColor: '#fce8d4',
          // Multi-colour scale for pie / mindmap / class & state groups.
          pie1: '#127c6d',
          pie2: '#c97d24',
          pie3: '#5b8def',
          pie4: '#b8392b',
          pie5: '#8b6fcb',
          pie6: '#219181',
          pie7: '#d9a441',
          pie8: '#4a9b6e',
          pie9: '#c2569b',
          pie10: '#6f6e66',
          pie11: '#e0b94d',
          pie12: '#7a8a99',
        },
      });
      return mermaid;
    });
    // If the dynamic import itself fails, don't cache the rejection forever —
    // let the next diagram retry the load (e.g. after a transient chunk 404).
    mermaidPromise.catch(() => {
      mermaidPromise = null;
    });
  }
  return mermaidPromise;
}

// Serialize renders. mermaid.render mutates shared global state and appends a
// temporary node to document.body, so two concurrent calls (multiple diagrams
// in a thread, or React StrictMode's double-invoked effects in dev) can corrupt
// each other and make *every* diagram fall back to raw text. A simple promise
// chain guarantees one render at a time without blocking the UI thread.
let renderQueue = Promise.resolve();
function renderSerial(id, content) {
  const run = renderQueue.then(async () => {
    const mermaid = await getMermaid();
    try {
      const { svg } = await mermaid.render(id, content);
      return svg;
    } finally {
      // mermaid.render appends a temporary element (#id, and #d{id} for its
      // error graphic on failure) to document.body. Belt-and-suspenders on
      // top of suppressErrorRendering: remove any leftover so a failed
      // diagram never strands a stray "bomb" node in the page.
      document.getElementById(id)?.remove();
      document.getElementById(`d${id}`)?.remove();
    }
  });
  // Keep the chain alive even when this render rejects, otherwise one bad
  // diagram would wedge the queue and block every diagram after it.
  renderQueue = run.then(
    () => {},
    () => {},
  );
  return run;
}

/**
 * Shared Mermaid renderer. Dynamically imports `mermaid` (kept out of the main
 * bundle) and renders the source to inline SVG. Falls back to showing the raw
 * source in a <pre> if the syntax doesn't parse, and shows a lightweight
 * placeholder while the async render is in flight.
 *
 * Used by both the Artifacts board (diagram artifacts) and inline chat messages
 * (```mermaid fenced blocks), plus the Decision Graph panel.
 */
export default function Mermaid({ content, className = 'diagram-rendered' }) {
  const source = stripCodeFence(content);
  const [svg, setSvg] = useState(() => svgCache.get(source) ?? null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (svgCache.has(source)) {
      setSvg(svgCache.get(source));
      setError(false);
      return;
    }
    let cancelled = false;
    setSvg(null);
    setError(false);
    const id = `mermaid-${Date.now()}-${mermaidSeq++}`;
    renderSerial(id, source)
      .then((rendered) => {
        if (!cancelled) {
          svgCache.set(source, rendered);
          setSvg(rendered);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // Don't swallow the reason — a silent catch turned every render
        // failure into an indistinguishable "raw text" blob with no clue
        // why. Log it so a broken diagram (bad syntax, chunk-load error,
        // browser quirk) is actually diagnosable.
        console.error('Mermaid render failed:', err);
        setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  if (error) {
    return <pre className="diagram-fallback">{source}</pre>;
  }
  if (svg === null) {
    return <div className="diagram-loading">Rendering diagram...</div>;
  }
  return <div className={className} dangerouslySetInnerHTML={{ __html: svg }} />;
}
